import { useCallback, useEffect, useRef, useState } from "react";
import { useLocalParticipant } from "@livekit/components-react";
import {
    Track,
    type LocalAudioTrack,
    type LocalTrack,
    type LocalVideoTrack,
} from "livekit-client";

/**
 * Why the share failed, when it is worth saying so.
 *
 * `noAudio` is the one that will happen most: the browser only puts the audio
 * in the stream when the person sharing ticks the box, and a therapist who
 * misses it gets silence with no idea why. `failed` covers everything else.
 * Cancelling the picker is not a failure and produces neither.
 */
export type ScreenAudioShareError = "noAudio" | "failed";

const isVideoTrack = (track: LocalTrack): track is LocalVideoTrack =>
    track.kind === Track.Kind.Video;

const isAudioTrack = (track: LocalTrack): track is LocalAudioTrack =>
    track.kind === Track.Kind.Audio;

/**
 * Sharing the sound of a browser tab without sharing the picture.
 *
 * A therapist often wants the patient to hear something — a guided
 * relaxation, a piece of music, a recording — while both of them stay on
 * camera. Screen sharing does carry the tab's audio, but it also throws a
 * screenshot of the therapist's browser onto the patient's screen and takes
 * the faces away, which is the opposite of what that moment needs.
 *
 * There is no audio-only capture API: `getDisplayMedia` always returns a
 * video track, and the tab picker is the only way the browser will hand over
 * tab audio. So the capture is the normal one and only the audio half is
 * published. The video track is kept — alive but never published, and never
 * seen by anyone — because in Chrome the capture session belongs to the
 * video track: end it and the audio goes quiet with it. It is stopped when
 * the sharing stops, which is also what closes the browser's own sharing bar.
 *
 * The published track is `ScreenShareAudio`, the same source a screen share
 * with audio publishes, so the other side needs nothing new: LiveKit's
 * `RoomAudioRenderer` already plays it.
 */
export function useScreenAudioShare() {
    const { localParticipant } = useLocalParticipant();

    const [sharing, setSharing] = useState(false);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<ScreenAudioShareError | null>(null);

    // What the capture handed us, so that stopping can undo all of it — the
    // published audio and the video that is only holding the session open.
    const captured = useRef<{
        audio: LocalAudioTrack;
        video: LocalVideoTrack | null;
    } | null>(null);

    const stop = useCallback(async () => {
        const tracks = captured.current;
        captured.current = null;
        setSharing(false);

        if (!tracks) return;

        tracks.video?.stop();
        try {
            await localParticipant.unpublishTrack(tracks.audio, true);
        } catch {
            // The room may already have taken the track down — on a
            // disconnect, or when the browser ended the capture itself.
            tracks.audio.stop();
        }
    }, [localParticipant]);

    const start = useCallback(async () => {
        setError(null);
        setPending(true);

        try {
            const tracks = await localParticipant.createScreenTracks({
                audio: true,
            });
            const audio = tracks.find(isAudioTrack) ?? null;
            const video = tracks.find(isVideoTrack) ?? null;

            // The picker was answered, but without the audio box ticked — or
            // by a browser that cannot capture audio at all. Nothing to
            // publish, and the capture is not worth keeping open.
            if (!audio) {
                video?.stop();
                setError("noAudio");
                return;
            }

            await localParticipant.publishTrack(audio);
            captured.current = { audio, video };
            setSharing(true);
        } catch (cause) {
            // Closing the picker is an answer, not a fault, and saying
            // anything about it would be noise.
            if (cause instanceof DOMException && cause.name === "NotAllowedError") {
                return;
            }
            setError("failed");
        } finally {
            setPending(false);
        }
    }, [localParticipant]);

    const toggle = useCallback(() => {
        void (sharing ? stop() : start());
    }, [sharing, start, stop]);

    // The browser's own "Stop sharing" bar ends the capture without telling
    // the page, so the control has to follow the track rather than its own
    // last click — otherwise it keeps offering to stop something that has
    // already stopped.
    useEffect(() => {
        const tracks = captured.current;
        if (!sharing || !tracks) return;

        const ended = () => void stop();
        tracks.audio.mediaStreamTrack.addEventListener("ended", ended);
        return () => {
            tracks.audio.mediaStreamTrack.removeEventListener("ended", ended);
        };
    }, [sharing, stop]);

    // Leaving the room mid-share should not leave a capture running behind
    // the closed page.
    useEffect(() => {
        return () => {
            captured.current?.video?.stop();
            captured.current?.audio.stop();
            captured.current = null;
        };
    }, []);

    return { sharing, pending, error, toggle } as const;
}
